import { deleteUserPreferences, getUserPreferences, updateUserPreferences } from '@ukpostbox/client-data';
import React, { PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { AuthContext } from 'react-oauth2-code-pkce';
import { useNavigate } from 'react-router';
import { FrameworkStorageKeysType } from '@ukpostbox/frontend-framework/lib/FrameworkStorageKeys';
import { WipeAuthSessionStorage } from '../AuthSettings';
import { useAuthorisedUserStore } from './Stores/AuthorisedUserStore';
import { ParseStringyObjectBooleans } from '../../_Global/Utils/Parsers';
import { AuthorisedUserContextType, AuthorisedUserProviderType } from '../Types/AuthorisedUserProviderTypes';

const AuthorisedUserContext = React.createContext<AuthorisedUserContextType>({} as AuthorisedUserContextType);

function AuthorisedUserProvider(props: PropsWithChildren<AuthorisedUserProviderType>) {
	const navigate = useNavigate();

	const { loginInProgress, token } = useContext(AuthContext);

	const dispatchAuthorisedUserStoreUpdate = useAuthorisedUserStore(
		(state) => state.dispatchAuthorisedUserStoreUpdate,
	);

	const GetCurrentUserPreferences = async () => {
		await getUserPreferences()
			.then((r) => {
				if (r) {
					dispatchAuthorisedUserStoreUpdate({
						type: 'setUserPreferences',
						payload: ParseStringyObjectBooleans(r),
					});
				}
			})
			.catch((err) => console.error(err));
	};

	const UpdateUserPreference = async (preference: Partial<Record<FrameworkStorageKeysType, any>>) => {
		await updateUserPreferences({ preferences: preference })
			.then(() => {
				dispatchAuthorisedUserStoreUpdate({
					type: 'updateUserPreferences',
					payload: preference,
				});
			})
			.catch((err) => console.error(err, preference));
	};

	const DeleteUserPreferences = async () => {
		await deleteUserPreferences()
			.then(() => {
				dispatchAuthorisedUserStoreUpdate({
					type: 'resetUserPreferences',
				});
			})
			.catch((err) => console.error(err));
	};

	const GetCurrentUserData = async () => {
		/* await getCurrentUser()
			.then((r) => {
				dispatchAuthorisedUserStoreUpdate({
					type: 'setCurrentUser',
					payload: r,
				});

				Sentry.setUser({ username: r.name, id: r.id });
			})
			.catch((err) => console.error(err)); */
	};

	const ClearSessionStore = () => {
		WipeAuthSessionStorage();
	};

	const RedirectError = () => {
		ClearSessionStore();
		const lastPath = localStorage.getItem('lastPath');
		navigate(lastPath || '/', { replace: true });
	};

	useEffect(() => {
		if (token && !loginInProgress) {
			GetCurrentUserData().then(() => null);
			GetCurrentUserPreferences().then(() => null);
		}
	}, [token, loginInProgress]);

	const authMemo = useMemo(
		() => ({
			loginInProgress,
			token,

			RedirectError,
			ClearSessionStore,
			UpdateUserPreference,
			DeleteUserPreferences,
		}),
		[loginInProgress, token],
	);

	return <AuthorisedUserContext.Provider value={authMemo}> {token && props.children}</AuthorisedUserContext.Provider>;
}

const useAuthorisedUser = () => useContext(AuthorisedUserContext);

export { AuthorisedUserContext, useAuthorisedUser };

export default AuthorisedUserProvider;
